import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut, User } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import IdentificationCard from '../../assets/icons/IdentificationCard.svg';
import CalendarCheck from '../../assets/icons/CalendarCheck.svg';
import ClockUser from '../../assets/icons/ClockUser.svg';
import MobileMenu from '../../components/MobileMenu';

const IndexPage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userName, setUserName] = useState<string>('');
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        try {
          const userProfileRef = doc(db, 'user_profiles', user.uid);
          const userProfileSnap = await getDoc(userProfileRef);
          
          if (userProfileSnap.exists()) {
            const userData = userProfileSnap.data();
            setUserName(userData.name || '');
          }
        } catch (error) {
          console.error('사용자 프로필을 가져오는 중 오류 발생:', error);
        }
      } else {
        setUser(null);
        setUserName('');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (!userMenuRef.current?.contains(target)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    try {
      setIsUserMenuOpen(false);
      await signOut(getAuth());
      navigate('/signin', { replace: true });
    } catch (error) {
      console.error('로그아웃 중 오류 발생:', error);
      alert('로그아웃 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FD]">
      {/* 헤더 */}
      <header className="h-[60px] lg:h-[80px] bg-white shadow-sm fixed lg:static top-0 left-0 right-0 z-50">
        <div className="max-w-[1380px] mx-auto px-4 sm:px-8 lg:px-16 h-full flex items-center justify-between">
          {/* 햄버거 버튼 - 태블릿/모바일에서 표시 */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden w-5 h-5 flex flex-col justify-center items-center gap-1"
            aria-label="메뉴 열기"
          >
            <span className="w-4 h-0.5 bg-[#1A253F]"></span>
            <span className="w-4 h-0.5 bg-[#1A253F]"></span>
            <span className="w-4 h-0.5 bg-[#1A253F]"></span>
          </button>

          {/* 타이틀 - 모바일에서는 중앙 정렬 */}
          <h1 className="text-[18px] lg:text-[28px] font-extrabold text-[#1A253F] font-pretendard text-center lg:text-left flex-1 lg:flex-none">
            부산항신항시설관리센터
          </h1>

          <nav className="hidden lg:flex items-center gap-[60px]">
            <Link to="/guide" className="text-[16px] xl:text-[20px] font-medium text-[#1A253F] font-pretendard whitespace-nowrap">이용안내</Link>
            <Link to="/facility" className="text-[16px] xl:text-[20px] font-medium text-[#1A253F] font-pretendard whitespace-nowrap">시설예약</Link>
            <Link to="/portville" className="text-[16px] xl:text-[20px] font-medium text-[#1A253F] font-pretendard whitespace-nowrap">포트빌입주</Link>
            <Link to="/parking" className="text-[16px] xl:text-[20px] font-medium text-[#1A253F] font-pretendard whitespace-nowrap">화물주차장</Link>
            <Link to="/notices" className="text-[16px] xl:text-[20px] font-medium text-[#1A253F] font-pretendard whitespace-nowrap">공지사항</Link>
          </nav>
          {user ? (
            <div className="hidden lg:block relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setIsUserMenuOpen((open) => !open)}
                className="inline-flex items-center gap-2 text-[16px] xl:text-[20px] font-bold text-[#1A253F] font-pretendard whitespace-nowrap"
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
              >
                <span>{userName} 님</span>
                <span className="text-[12px] xl:text-[14px] text-[#5A6A87]">▾</span>
              </button>

              {isUserMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-3 w-[190px] rounded-md border border-[#EAEAEA] bg-white shadow-lg overflow-hidden z-[60]"
                >
                  <Link
                    to="/settings"
                    role="menuitem"
                    className="block px-4 py-3 text-[14px] font-semibold text-[#1A253F] hover:bg-[#F7F9FD]"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    설정
                  </Link>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-4 py-3 text-[14px] font-semibold text-[#1A253F] hover:bg-[#F7F9FD]"
                    onClick={handleLogout}
                  >
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/signin" className="hidden lg:block text-[16px] xl:text-[20px] font-bold text-[#1A253F] font-pretendard whitespace-nowrap">로그인</Link>
          )}
        </div>
      </header>

      {/* 메인 배너 */}
      <div className="relative h-[640px]">
        <div className="absolute inset-0 bg-[#D9D9D9]">
          <img 
            src="/images/main-banner.jpg" 
            alt="부산항 신항만 시설관리센터 메인 배너" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-[#061325]/80 via-[#001F4B]/40 to-transparent" />
        
        <div className="relative max-w-[1380px] mx-auto px-4 sm:px-8 lg:px-16 pt-[80px] lg:pt-[140px]">

          <div className="w-[304px] lg:w-[590px] h-[2px] lg:h-[5px] bg-white shadow-lg mb-[16px] lg:mb-[20px]" />
          <h2 className="text-[24px] lg:text-[48px] font-extrabold text-white font-pretendard mb-[8px] lg:mb-[12px] leading-[1.2] lg:leading-[0.79] drop-shadow-lg">
            글로벌허브 항만시설관리
          </h2>
          <h3 className="text-[24px] lg:text-[48px] font-extrabold text-white font-pretendard leading-[1.2] lg:leading-[0.79] drop-shadow-lg">
            부산항신항시설관리센터입니다<span className="inline-block w-[8px] lg:w-[15px] h-[8px] lg:h-[15px] rounded-full bg-[#FFD800] ml-2 align-text-bottom lg:align-bottom"></span>
          </h3>
        </div> 
      </div>

      {/* 서비스 카드 섹션 */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16 -mt-[440px] lg:-mt-[240px] relative z-10">
        <div className="bg-transparent lg:bg-white rounded-sm shadow-none lg:shadow-lg p-6 max-w-[1240px] mx-auto lg:block">
          <div className="flex flex-col lg:flex-row flex-wrap gap-6">
            {/* 화물주차장/로그인 카드 */}
            <div className="basis-0 flex-1 min-w-0 p-6 bg-white rounded-sm shadow-lg lg:shadow-none lg:bg-transparent">
              <div className="flex flex-col items-center">
                <div className="w-full max-w-[244px] lg:w-auto flex lg:flex-col items-start lg:items-center justify-start lg:justify-center gap-4 mb-6 lg:mb-4">
                  <img src={IdentificationCard} alt={user ? "설정 아이콘" : "로그인 아이콘"} className="w-[50px] lg:w-[54px] h-[50px] lg:h-[54px]" />
                  <div className="flex flex-col items-start lg:items-center">
                    <h3 className="text-[18px] lg:text-[26px] font-extrabold text-[#1A253F] font-pretendard mb-1">
                      {user ? '화물주차장 신청' : '로그인'}
                    </h3>
                    <p className="text-[11px] lg:text-[14px] text-[#5A6A87] font-pretendard">
                      {user ? 'Parking' : 'Login'}
                    </p>
                  </div>
                </div>
                <Link
                  to={user ? "/parking" : "/signin"}
                  className="w-full max-w-[244px] lg:w-[230px] h-[40px] lg:h-[50px] bg-[#155ACB] rounded-sm text-white text-[14px] lg:text-[16px] font-bold font-pretendard flex items-center justify-center"
                >
                  {user ? '신청하기' : '로그인'}
                </Link>
              </div>
            </div>

            <div className="hidden lg:block w-[1px] h-[220px] bg-[#EAEAEA]" />

            {/* 시설예약 카드 */}
            <div className="basis-0 flex-1 min-w-0 p-6 bg-white rounded-sm shadow-lg lg:shadow-none lg:bg-transparent">
              <div className="flex flex-col items-center">
                <div className="w-full max-w-[244px] lg:w-auto flex lg:flex-col items-start lg:items-center justify-start lg:justify-center gap-4 mb-6 lg:mb-4">
                  <img src={CalendarCheck} alt="시설예약 아이콘" className="w-[50px] lg:w-[54px] h-[50px] lg:h-[54px]" />
                  <div className="flex flex-col items-start lg:items-center">
                    <h3 className="text-[18px] lg:text-[26px] font-extrabold text-[#1A253F] font-pretendard mb-1">
                      {user ? '시설예약' : '이용안내'}
                    </h3>
                    <p className="text-[11px] lg:text-[14px] text-[#5A6A87] font-pretendard">
                      {user ? 'Facility Reservation' : 'Guide'}
                    </p>
                  </div>
                </div>
                <Link
                  to={user ? "/facility" : "/guide"}
                  className="w-full max-w-[244px] lg:w-[230px] h-[40px] lg:h-[50px] bg-[#155ACB] rounded-sm text-white text-[14px] lg:text-[16px] font-bold font-pretendard flex items-center justify-center"
                >
                  {user ? '예약하기' : '안내보기'}
                </Link>
              </div>
            </div>

            <div className="hidden lg:block w-[1px] h-[220px] bg-[#EAEAEA]" />

            {/* 포트빌 입주 신청 카드 */}
            <div className="basis-0 flex-1 min-w-0 p-6 bg-white rounded-sm shadow-lg lg:shadow-none lg:bg-transparent">
              <div className="flex flex-col items-center">
                <div className="w-full max-w-[244px] lg:w-auto flex lg:flex-col items-start lg:items-center justify-start lg:justify-center gap-4 mb-6 lg:mb-4">
                  <img src={ClockUser} alt="포트빌 입주 신청 아이콘" className="w-[50px] lg:w-[54px] h-[50px] lg:h-[54px]" />
                  <div className="flex flex-col items-start lg:items-center">
                    <h3 className="text-[18px] lg:text-[26px] font-extrabold text-[#1A253F] font-pretendard mb-1">
                      {user ? '포트빌 입주 신청' : '회원가입'}
                    </h3>
                    <p className="text-[11px] lg:text-[14px] text-[#5A6A87] font-pretendard">
                      {user ? 'Portville Standby' : 'Sign Up'}
                    </p>
                  </div>
                </div>
                <Link
                  to={user ? "/portville" : "/signup"}
                  className="w-full max-w-[244px] lg:w-[230px] h-[40px] lg:h-[50px] bg-[#155ACB] rounded-sm text-white text-[14px] lg:text-[16px] font-bold font-pretendard flex items-center justify-center"
                >
                  {user ? '신청하기' : '가입하기'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 푸터 */}
      <footer className="bg-[#0E2039] h-[340px] lg:h-[210px] mt-0 lg:mt-[100px]">
        <div className="max-w-[1380px] mx-auto px-4 sm:px-8 lg:px-16 pt-[40px] lg:pt-[54px]">
          <div className="flex flex-col">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-[8px] lg:gap-[44px] mb-[41px] lg:mb-[20px]">
              <h4 className="text-[16px] lg:text-[24px] font-semibold text-white/80 font-pretendard">
                부산항신항시설관리센터
              </h4>
              <div className="flex flex-col lg:flex-row gap-[8px] lg:gap-[44px] lg:ml-auto">
                <Link to="/notices" className="text-[14px] lg:text-[15px] font-bold text-white font-pretendard">공지사항</Link>
                <Link to="/privacy" className="text-[14px] lg:text-[15px] font-bold text-white font-pretendard">개인정보처리방침</Link>
                <Link to="/email-policy" className="text-[14px] lg:text-[15px] font-bold text-white font-pretendard">이메일무단수집거부</Link>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-[8px] lg:gap-[24px]">
              <p className="text-[14px] lg:text-[15px] font-medium text-[#64AAC6] font-pretendard">Contact</p>
              <p className="text-[12px] font-light text-white/40 font-pretendard">
                부산광역시 강서구 신항로 96-87
              </p>
              <div className="flex items-center gap-[8px]">
                <p className="text-[12px] font-light text-white/40 font-pretendard">
                  대표번호:051-890-7400
                </p>
                <p className="text-[12px] font-light text-white/40 font-pretendard">
                  FAX:051-831-5344
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* 모바일 메뉴 */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onLogout={handleLogout}
        isLoggedIn={!!user}
        userName={userName}
      />
    </div>
  );
};

export default IndexPage; 
